import React, {useState, useEffect, useRef} from 'react';
import {useTranslation} from "react-i18next";
import {generatePseudo} from '@/utils/pseudoGenerator.js';
import {Captcha} from "@/components/Captcha.jsx";
import {Link} from "react-router-dom";

export const StepForm = ({
                             userInfo,
                             setUserInfo,
                             userScore = {},
                             isLoading,
                             apiError,
                             handleFormSubmit,
                             onBack,
                             isDemo = false,
                             title,        // override the header title (e.g. penalty latecomer)
                             description,  // override the score-based subtitle
                             icon = '⚡️',
                             backLabel     // override the back-button label (default = "change my score")
                         }) => {

    const {t} = useTranslation();
    const formRef = useRef(null);
    const captchaRef = useRef(null);

    const [captchaToken, setCaptchaToken] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);

    const skipCaptcha = isDemo || import.meta.env.DEV;

    useEffect(() => {
        window.scrollTo({top: 0, behavior: 'smooth'});
    }, []);

    const handleFocus = (e) => {
        const target = e.target;
        setTimeout(() => {
            target.scrollIntoView({behavior: 'smooth', block: 'center'});
        }, 300);
    };

    const displayError = React.useMemo(() => {
        if (!apiError) return null;
        if (typeof apiError === 'object') {
            if (apiError.data && apiError.data.email) return t('form.error_email_invalid');
        }
        return t('form.generic_error');
    }, [apiError, t]);

    useEffect(() => {
        if (!apiError) return;
        const errorString = JSON.stringify(apiError);
        const errorCode = apiError.error || "";

        if (
            errorString.includes("ALREADY_PLAYED") ||
            errorString.includes("ParticipantAlreadyPlayedException") ||
            errorCode === "conflict"
        ) {
            setIsAlreadyRegistered(true);
        } else {
            setCaptchaToken(null);
            captchaRef.current?.reset();
        }
    }, [apiError]);

    useEffect(() => {
        if (!userInfo.pseudo) {
            setUserInfo({...userInfo, pseudo: generatePseudo()});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAutoPseudo = (e) => {
        e.preventDefault();
        setIsAnimating(true);
        setUserInfo({...userInfo, pseudo: generatePseudo()});
        setTimeout(() => setIsAnimating(false), 300);
    };

    const onFormSubmit = (e) => {
        e.preventDefault();
        // Le navigateur a déjà vérifié l'email et la checkbox grâce aux attributs "required"
        // On bloque manuellement que pour le captcha si besoin
        if (!skipCaptcha && !captchaToken) return;

        handleFormSubmit(e, {captchaToken: skipCaptcha ? 'bypass-token' : captchaToken});
    };

    if (isAlreadyRegistered) {
        return (
            <div className="mt-8 animate-fade-in bg-white p-8 rounded-2xl shadow-soft text-center">
                <div
                    className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h8"/>
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                        <path d="m16 19 2 2 4-4"/>
                    </svg>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {t('form.already_registered.title')}
                </h3>

                <p className="text-gray-600 mb-6 leading-relaxed">
                    {t('form.already_registered.description')} <span
                    className="font-bold text-gray-800">{userInfo.email}</span>.
                </p>

                <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl mb-6">
                    <p className="text-sm text-blue-800 font-medium">
                        {t('form.already_registered.email_sent')}
                    </p>
                </div>

                <p className="text-xs text-gray-400 mb-6">
                    {t('form.already_registered.spam_hint')}
                </p>

                <button
                    onClick={onBack}
                    className="w-full py-3 bg-brand-dark text-white font-bold rounded-xl shadow-lg hover:bg-gray-800 transition-all active:scale-95"
                >
                    {t('form.already_registered.back_btn')}
                </button>
            </div>
        );
    }

    return (
        <div
            ref={formRef}
            className="mt-8 animate-fade-in bg-white p-6 rounded-2xl shadow-soft"
        >
            <div className="text-center mb-6">
                <div
                    className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl">
                    {icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900">{title ?? t('form.title')}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                    {description ?? (
                        <>{t('form.description_1')} <span
                            className="font-bold text-black">{userScore.team1}-{userScore.team2}</span>.</>
                    )}
                </p>
            </div>

            <form onSubmit={onFormSubmit} className="space-y-4">

                {/* --- INPUT PSEUDO --- */}
                <div className="relative group">
                    <input
                        type="text"
                        placeholder={t('form.placeholder_pseudo')}
                        value={userInfo.pseudo}
                        onChange={(e) => setUserInfo({...userInfo, pseudo: e.target.value})}
                        onFocus={handleFocus}
                        required
                        minLength={3}
                        disabled={isLoading}
                        className={`w-full px-4 py-3 pr-24 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-black focus:ring-1 focus:ring-black text-gray-800 font-medium disabled:opacity-50 transition-all ${isAnimating ? 'bg-green-50' : ''}`}
                    />
                    <button
                        type="button"
                        onClick={handleAutoPseudo}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white border border-gray-200 text-xs font-bold text-gray-500 hover:text-black hover:border-black px-3 py-1.5 rounded-lg shadow-sm active:scale-95 transition-all flex items-center gap-1"
                    >
                        <span className={isAnimating ? 'animate-pulse' : ''}>⚡️</span>
                        <span>Auto</span>
                    </button>
                </div>

                {/* --- INPUT EMAIL --- */}
                <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder={t('form.placeholder_email')}
                    value={userInfo.email}
                    onChange={(e) => setUserInfo({...userInfo, email: e.target.value})}
                    onFocus={handleFocus}
                    required
                    disabled={isLoading}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-black focus:ring-1 focus:ring-black text-gray-800 font-medium disabled:opacity-50"
                />

                {!isDemo && (
                    <label className="flex items-start gap-3 p-1 cursor-pointer group">
                        <div className={`relative flex items-center justify-center w-5 h-5 mt-0.5 rounded border transition-colors shrink-0 
                        ${userInfo.acceptTerms ? 'bg-action-green border-action-green' : 'bg-gray-50 border-gray-300 group-hover:border-black'}`}
                        >
                            <input
                                type="checkbox"
                                required // 💡 Ceci déclenchera la bulle d'erreur native si non coché
                                checked={userInfo.acceptTerms || false}
                                onChange={(e) => setUserInfo({...userInfo, acceptTerms: e.target.checked})}
                                disabled={isLoading}
                                className="absolute opacity-0 cursor-pointer w-full h-full"
                            />
                            {userInfo.acceptTerms && (
                                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24"
                                     stroke="currentColor" strokeWidth="3">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                                </svg>
                            )}
                        </div>
                        <span className="text-xs text-gray-500 leading-relaxed">
                        J'accepte les <Link to="/cgu" target="_blank" rel="noopener noreferrer"
                                            className="text-black font-bold underline hover:text-action-green transition-colors">CGU</Link> et le <Link
                            to="/rules" target="_blank" rel="noopener noreferrer"
                            className="text-black font-bold underline hover:text-action-green transition-colors">Règlement du Jeu Promotionnel</Link>.
                    </span>
                    </label>)}

                {displayError && !isAlreadyRegistered && (
                    <div
                        className="bg-red-50 text-red-600 text-sm p-3 rounded-lg border border-red-200 font-bold text-center">
                        {displayError}
                    </div>
                )}

                {/* 💡 AFFICHE LE CAPTCHA UNIQUEMENT SI ON N'EST PAS EN DEV / DEMO */}
                {!skipCaptcha && (
                    <Captcha
                        ref={captchaRef}
                        onVerify={setCaptchaToken}
                    />
                )}

                <button
                    id="demo-btn-submit"
                    type="submit"
                    disabled={isLoading || (!skipCaptcha && !captchaToken)}
                    className={`w-full py-4 font-bold rounded-xl text-lg shadow-lg transition-all flex items-center justify-center
                            ${(isLoading || (!skipCaptcha && !captchaToken))
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                        : 'bg-action-green text-white hover:brightness-110 active:scale-98 shadow-md'
                    }`}>
                    {isLoading ? t('form.loading_btn') : t('form.submit_btn')}
                </button>
            </form>

            <button
                onClick={() => !isLoading && onBack()}
                disabled={isLoading}
                className="w-full mt-4 py-2 text-sm text-gray-400 underline hover:text-gray-600 disabled:opacity-50 transition-colors"
            >
                {backLabel ?? t('form.modify_btn')}
            </button>
        </div>
    );
};